/** One entry Recharts passes a tooltip's `content` for each visible series. */
interface ChartTooltipEntry {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string | (number | string)[];
  color?: string;
}

/**
 * A chart tooltip in the shared `.chart-tooltip` look: the point's label in bold, then a row per series with its colour, its name
 * and its value. A series filled with a gradient reports a `url(#…)` reference as its colour, which cannot colour the row's square,
 * so `colors` gives each series' own colour by data key. Rows without a value are left out.
 *
 * @param props.active - Whether the tooltip is showing, from Recharts.
 * @param props.payload - The series at the hovered point, from Recharts.
 * @param props.label - The hovered point's label, from Recharts.
 * @param props.colors - Each series' colour by data key (or by name, with `keyBy="name"`), for series whose reported colour is not one.
 * @param props.keyBy - What `colors` is keyed by: the data key, or the series' name (a pie's slices share one data key).
 * @param props.formatLabel - Turns the point's label into the heading.
 * @param props.formatValue - Turns a series' value into the text shown.
 * @returns The tooltip, or nothing when it is not showing.
 */
export function ChartTooltip({
  active,
  payload,
  label,
  colors,
  keyBy = "dataKey",
  formatLabel,
  formatValue,
}: {
  active?: boolean;
  payload?: ChartTooltipEntry[];
  label?: string | number;
  colors?: ReadonlyMap<string, string>;
  keyBy?: "dataKey" | "name";
  formatLabel: (label: string) => string;
  formatValue: (value: number) => string;
}): React.JSX.Element | null {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <div className="chart-tooltip">
      {label !== undefined && formatLabel(String(label)) !== "" && <p className="chart-tooltip-label">{formatLabel(String(label))}</p>}
      <ul>
        {payload.map((entry) =>
          entry.value === null || entry.value === undefined ? null : (
            <li key={String(entry.name ?? entry.dataKey)} style={{ color: colors?.get(String(keyBy === "name" ? entry.name : entry.dataKey)) ?? entry.color }}>
              <span className="chart-tooltip-name">{String(entry.name)}</span>
              <span className="chart-tooltip-value">{formatValue(Number(entry.value))}</span>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}
